import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  TemplateRef,
  signal,
  computed,
  OnDestroy,
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  OverlayModule,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewContainerRef } from '@angular/core';

@Component({
  selector: 'app-custom-dropdown-v2',
  templateUrl: './custom-dropdown-v2.component.html',
  styleUrl: './custom-dropdown-v2.component.scss',
  standalone: true,
  imports: [CommonModule, OverlayModule],
})
export class DropdownFieldV2Component implements OnDestroy {
  @Input() options: { label: string; value: any }[] = [];
  @Input() label = '';
  @Input() placeholder = '== Select an option ==';
  @Input() selectedText = '';
  @Input() hasError = false;
  @Input() onTouchTextErrorColor = false;
  @Input() errorMessage = '';
  @Input() openArrowClass = 'orange-text';
  @Input() closeArrowClass = 'violet-text';
  @Input() showError = false;
  @Input() labelWidth = '130px';
  @Input() onTouched: () => void = () => {};
  @Output() selectionChange = new EventEmitter<{ label: string; value: any }>();

  @ViewChild('trigger', { read: ElementRef }) triggerRef!: ElementRef;
  @ViewChild('dropdownPanel') panelTemplate!: TemplateRef<any>;
  @ViewChild('cardTrigger', { read: ElementRef }) cardTriggerRef!: ElementRef;

  isOpen = signal(false);
  openedUp = signal(false);
  isTouched = signal(false);
  arrowIcon = computed(() => (this.isOpen() ? 'arrow_drop_up' : 'arrow_drop_down'));
  arrowClass = computed(() => (this.isOpen() ? this.openArrowClass : this.closeArrowClass));

  private overlayRef: OverlayRef | null = null;

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef,
    private elRef: ElementRef,
  ) {}

  ngOnDestroy(): void {
    this.closePanel();
    this.overlayRef?.dispose();
  }

  toggleDropdown(): void {
    this.isOpen() ? this.closePanel() : this.openPanel();
  }

  openPanel(): void {
    if (this.overlayRef?.hasAttached()) {
      return;
    }

    const cardEl = this.cardTriggerRef.nativeElement as HTMLElement;

    const positions: ConnectedPosition[] = [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
      { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom' },
    ];

    const positionStrategy = this.overlay
    .position()
    .flexibleConnectedTo(cardEl)  // cardEl is now #cardTrigger = dropdown-host
    .withPositions(positions)
    .withPush(false)
    .withDefaultOffsetY(0);    

    // CDK reports which position it picked: origin top = opened upward
    positionStrategy.positionChanges.subscribe((change) => {
      this.openedUp.set(change.connectionPair.originY === 'top');
    });

    const scrollStrategy = this.overlay.scrollStrategies.reposition();

    const cardRect = cardEl.getBoundingClientRect();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      minWidth: cardRect.width,
      maxWidth: cardRect.width,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.overlayRef.backdropClick().subscribe(() => {
      this.closePanel();
      this.markTouched();
    });

    this.overlayRef.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') {
        this.closePanel();
        this.markTouched();
      }
    });

    const portal = new TemplatePortal(this.panelTemplate, this.vcr);
    this.overlayRef.attach(portal);
    this.isOpen.set(true);
  }

  closePanel(): void {
    this.overlayRef?.detach();
    this.isOpen.set(false);
    this.openedUp.set(false);
  }

  onSelect(option: { label: string; value: any }): void {
    this.selectedText = option.label;
    this.isTouched.set(false);
    this.closePanel();
    this.selectionChange.emit(option);
  }

  private markTouched(): void {
    if (!this.showError) { return; } 
    if (!this.selectedText) {
      this.isTouched.set(true);
      this.onTouched();
    }
  }

  reset(): void {
    this.selectedText = '';
    this.isTouched.set(false);
    this.closePanel();
  }
}
